import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import "../styles/PdamBillCalculator.css";

const API_BASE_URL = "https://depotaircerdas.com/api/pdam";

const formatRupiah = (amount) => {
  if (typeof amount !== "number" || isNaN(amount)) return "Rp 0";
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(amount);
};

const PdamBillCalculator = () => {
  const [pdamOptions, setPdamOptions] = useState([]);
  const [selectedPdam, setSelectedPdam] = useState("");
  const [golonganOptions, setGolonganOptions] = useState([]);
  const [selectedGolongan, setSelectedGolongan] = useState("");

  const [username, setUsername] = useState(localStorage.getItem("username") || "");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [usageM3, setUsageM3] = useState(null);
  const [billResult, setBillResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const currentYear = new Date().getFullYear();

  const years = useMemo(() => {
    return Array.from({ length: 11 }, (_, i) => String(currentYear - 5 + i));
  }, [currentYear]);

  const months = [
    { value: "01", label: "Januari" },
    { value: "02", label: "Februari" },
    { value: "03", label: "Maret" },
    { value: "04", label: "April" },
    { value: "05", label: "Mei" },
    { value: "06", label: "Juni" },
    { value: "07", label: "Juli" },
    { value: "08", label: "Agustus" },
    { value: "09", label: "September" },
    { value: "10", label: "Oktober" },
    { value: "11", label: "November" },
    { value: "12", label: "Desember" },
  ];

  const selectedPeriodLabel = useMemo(() => {
    if (!selectedMonth || !selectedYear) return "-";
    const date = new Date(`${selectedYear}-${selectedMonth}-01T00:00:00`);
    return date.toLocaleDateString("id-ID", {
      month: "long",
      year: "numeric",
    });
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    const today = new Date();
    const currentMonthFormatted = String(today.getMonth() + 1).padStart(2, "0");
    const currentYearFormatted = String(today.getFullYear());

    setSelectedMonth(currentMonthFormatted);
    setSelectedYear(currentYearFormatted);

    const fetchInitialData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setError("Autentikasi diperlukan. Silakan login kembali.");
          setLoading(false);
          return;
        }

        setLoading(true);

        const pdamResponse = await axios.get(`${API_BASE_URL}/pdam-options`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const rawData = pdamResponse?.data?.data || {};
        const pdamDataArray = Object.entries(rawData).map(([pdamId, pdamInfo]) => ({
          pdamId,
          name: pdamInfo.name,
          golongan: Object.entries(pdamInfo.golongan || {}).map(([golonganId, description]) => ({
            golonganId,
            description,
          })),
        }));

        setPdamOptions(pdamDataArray);

        if (pdamDataArray.length > 0) {
          setSelectedPdam(pdamDataArray[0].pdamId);

          const defaultPdam = pdamDataArray[0];
          if (defaultPdam.golongan.length > 0) {
            setGolonganOptions(defaultPdam.golongan);
            setSelectedGolongan(defaultPdam.golongan[0].golonganId);
          } else {
            setGolonganOptions([]);
            setSelectedGolongan("");
          }
        } else {
          setPdamOptions([]);
          setSelectedPdam("");
          setGolonganOptions([]);
          setSelectedGolongan("");
          setError("Tidak ada data PDAM tersedia dari server.");
        }
      } catch (err) {
        console.error("Error fetching initial data (PDAM options):", err);
        setError("Gagal memuat opsi PDAM. Pastikan backend berjalan dan token valid.");
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    const pdam = pdamOptions.find((p) => p.pdamId === selectedPdam);
    if (pdam) {
      setGolonganOptions(pdam.golongan);

      if (pdam.golongan.length > 0) {
        setSelectedGolongan(pdam.golongan[0].golonganId);
      } else {
        setSelectedGolongan("");
      }
    }
  }, [selectedPdam, pdamOptions]);

  useEffect(() => {
    const fetchAndCalculateAutomatically = async () => {
      if (
        !username ||
        !selectedMonth ||
        !selectedYear ||
        !selectedPdam ||
        !selectedGolongan ||
        pdamOptions.length === 0
      ) {
        if (username && pdamOptions.length > 0) {
          setError("Harap lengkapi semua pilihan: Username, Bulan, Tahun, PDAM, dan Golongan.");
        }
        setUsageM3(null);
        setBillResult(null);
        setLoading(false);
        return;
      }

      const token = localStorage.getItem("token");
      if (!token) {
        setError("Anda tidak terautentikasi. Silakan login kembali.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");
      setUsageM3(null);
      setBillResult(null);

      try {
        let fetchedUsageM3 = null;

        try {
          const usageResponse = await axios.get(
            `${API_BASE_URL}/total-usage/${username}?month=${selectedMonth}&year=${selectedYear}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          if (
            usageResponse.data &&
            usageResponse.data.data &&
            typeof usageResponse.data.data.total_m3 === "number"
          ) {
            fetchedUsageM3 = usageResponse.data.data.total_m3;
            console.log(
              `Total penggunaan untuk ${username} di bulan ${selectedMonth}/${selectedYear}: ${fetchedUsageM3} m³`
            );

            if (fetchedUsageM3 === 0) {
              setError(
                "Tidak ada penggunaan air yang tercatat untuk periode ini. Pastikan username dan periode benar."
              );
              setUsageM3(0);
            }
          } else {
            setError("Format data penggunaan air tidak valid dari server atau data penggunaan kosong.");
            setUsageM3(0);
            setLoading(false);
            return;
          }
        } catch (usageErr) {
          console.error(
            "Error fetching usage:",
            usageErr.response ? usageErr.response.data : usageErr.message
          );
          setError(
            `Gagal mendapatkan data penggunaan air: ${
              usageErr.response
                ? usageErr.response.data.message || usageErr.response.data.error
                : usageErr.message
            }`
          );
          setUsageM3(0);
          setLoading(false);
          return;
        }

        setUsageM3(fetchedUsageM3);

        const billResponse = await axios.post(
          `${API_BASE_URL}/calculate-bill`,
          {
            pdamId: selectedPdam,
            golonganId: selectedGolongan,
            usageM3: fetchedUsageM3,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (billResponse.data && billResponse.data.data) {
          setBillResult(billResponse.data.data);
          console.log("Perhitungan tagihan berhasil!", billResponse.data.data);
        } else {
          setError("Format data tagihan tidak valid dari server.");
        }
      } catch (err) {
        console.error(
          "Error calculating bill (overall):",
          err.response ? err.response.data : err.message
        );
        setError(
          `Gagal menghitung tagihan: ${
            err.response ? err.response.data.message || err.response.data.error : err.message
          }`
        );
        setBillResult(null);
      } finally {
        setLoading(false);
      }
    };

    fetchAndCalculateAutomatically();
  }, [username, selectedMonth, selectedYear, selectedPdam, selectedGolongan, pdamOptions.length]);

  return (
    <div className="page-shell">
      <div className="page-hero-card">
        <span className="page-hero-badge">Perhitungan PDAM</span>
        <h2 className="page-hero-title">Perhitungan Tagihan PDAM</h2>
        <p className="page-hero-subtitle">
          Ini adalah estimasi tagihan berdasarkan penggunaan air dari sistem Anda dan tarif PDAM
          yang dipilih. Nilai ini bersifat perkiraan.
        </p>
      </div>

      {error && <div className="pdam-state-box error">{error}</div>}
      {loading && <div className="pdam-state-box">Memuat perhitungan tagihan...</div>}

      <div className="page-card">
        <div className="page-card-header">
          <div>
            <h3 className="section-title">Pengaturan Perhitungan</h3>
            <p className="section-subtitle">
              Pilih periode, PDAM, dan golongan tarif untuk menghitung estimasi tagihan.
            </p>
          </div>
        </div>

        <div className="pdam-filter-grid">
          <div className="form-group">
            <label>Username Depot</label>
            <input
              type="text"
              className="report-select"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              readOnly
              style={{ backgroundColor: "#f8fafc", cursor: "not-allowed" }}
            />
          </div>

          <div className="form-group">
            <label>Bulan</label>
            <select
              className="report-select"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              disabled={loading}
            >
              {months.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Tahun</label>
            <select
              className="report-select"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              disabled={loading}
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>PDAM</label>
            <select
              className="report-select"
              value={selectedPdam}
              onChange={(e) => setSelectedPdam(e.target.value)}
              disabled={loading || pdamOptions.length === 0}
            >
              {pdamOptions.map((pdam) => (
                <option key={pdam.pdamId} value={pdam.pdamId}>
                  {pdam.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Golongan</label>
            <select
              className="report-select"
              value={selectedGolongan}
              onChange={(e) => setSelectedGolongan(e.target.value)}
              disabled={loading || golonganOptions.length === 0}
            >
              {golonganOptions.map((gol) => (
                <option key={gol.golonganId} value={gol.golonganId}>
                  {gol.description}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="pdam-summary-grid">
        <div className="pdam-summary-card">
          <span>Periode</span>
          <strong>{selectedPeriodLabel}</strong>
          <small>Periode penggunaan aktif</small>
        </div>

        <div className="pdam-summary-card">
          <span>Penggunaan Air</span>
          <strong>{usageM3 !== null ? `${usageM3.toFixed(3)} m³` : "-"}</strong>
          <small>Diambil dari total penggunaan sensor</small>
        </div>

        <div className="pdam-summary-card">
          <span>PDAM</span>
          <strong>{pdamOptions.find((p) => p.pdamId === selectedPdam)?.name || "-"}</strong>
          <small>Tarif penyedia air</small>
        </div>

        <div className="pdam-summary-card">
          <span>Golongan</span>
          <strong>
            {golonganOptions.find((g) => g.golonganId === selectedGolongan)?.description || "-"}
          </strong>
          <small>Kategori pelanggan aktif</small>
        </div>
      </div>

      <div className="page-card">
        <div className="page-card-header">
          <div>
            <h3 className="section-title">Hasil Perhitungan Tagihan</h3>
            <p className="section-subtitle">
              Rincian estimasi biaya berdasarkan tarif dan penggunaan air.
            </p>
          </div>
        </div>

        {billResult ? (
          <div className="pdam-result-grid">
            <div className="pdam-result-card">
              <span>PDAM</span>
              <strong>{billResult.pdam_name}</strong>
            </div>
            <div className="pdam-result-card">
              <span>Golongan</span>
              <strong>{billResult.golongan}</strong>
            </div>
            <div className="pdam-result-card">
              <span>Penggunaan</span>
              <strong>{billResult.usage_m3.toFixed(3)} m³</strong>
            </div>
            <div className="pdam-result-card">
              <span>Total Tagihan</span>
              <strong>{formatRupiah(billResult.total_tagihan)}</strong>
            </div>

            <div className="pdam-result-card">
              <span>Biaya Pemakaian Air</span>
              <strong>{formatRupiah(billResult.detail_biaya.biaya_pemakaian_air)}</strong>
            </div>
            <div className="pdam-result-card">
              <span>Biaya Administrasi</span>
              <strong>{formatRupiah(billResult.detail_biaya.biaya_administrasi)}</strong>
            </div>
            <div className="pdam-result-card">
              <span>Biaya Pemeliharaan Meter</span>
              <strong>{formatRupiah(billResult.detail_biaya.biaya_pemeliharaan_meter)}</strong>
            </div>
            <div className="pdam-result-card">
              <span>Subtotal</span>
              <strong>{formatRupiah(billResult.detail_biaya.sub_total_sebelum_ppn_materai)}</strong>
            </div>
            <div className="pdam-result-card">
              <span>PPN ({billResult.detail_biaya.ppn_persentase}%)</span>
              <strong>{formatRupiah(billResult.detail_biaya.ppn_jumlah)}</strong>
            </div>
            <div className="pdam-result-card">
              <span>Materai</span>
              <strong>{formatRupiah(billResult.detail_biaya.materai_jumlah)}</strong>
            </div>
          </div>
        ) : (
          !loading && (
            <div className="pdam-state-box">
              Belum ada hasil perhitungan yang bisa ditampilkan.
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default PdamBillCalculator;